import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import {
  CAMPAIGN_ACTIVITY_TONES,
  CAMPAIGN_OWNER_ACCENTS,
} from '#/entities/campaign'
import type {
  CampaignActivityActor,
  CampaignActivityEntry,
  CampaignOwnerAccent,
} from '#/entities/campaign'
import { cn } from '#/shared/lib/utils'
import { TablePagination } from '#/shared/ui/table-pagination'

import { useCampaignActivity } from '../model/use-campaign-activity'
import { CampaignFilterChips } from './campaign-filter-chips'
import { CampaignActivitySkeleton } from './campaign-skeletons'

const ACCENT_FILL: Record<CampaignOwnerAccent, string> = {
  [CAMPAIGN_OWNER_ACCENTS.blue]: 'bg-campaign-running',
  [CAMPAIGN_OWNER_ACCENTS.green]: 'bg-campaign-owner-green',
  [CAMPAIGN_OWNER_ACCENTS.amber]: 'bg-campaign-owner-amber',
}

const TITLE_TONE = {
  [CAMPAIGN_ACTIVITY_TONES.neutral]: 'text-gray-900',
  [CAMPAIGN_ACTIVITY_TONES.attention]: 'text-campaign-attention',
  [CAMPAIGN_ACTIVITY_TONES.failure]: 'text-campaign-stopped',
} as const

/**
 * Who the entry belongs to. A system entry gets a neutral dot rather than an
 * initial — it is not a person, and giving it a letter would imply one.
 */
function ActorCell({ actor }: { actor?: CampaignActivityActor }) {
  const { t } = useTranslation()
  const name = actor?.name ?? t('campaigns.activity.system')
  return (
    <span className="flex w-37.5 shrink-0 items-center gap-2">
      <span
        aria-hidden="true"
        className={cn(
          'flex size-5.5 shrink-0 items-center justify-center rounded-full text-2xs font-medium text-white',
          actor ? ACCENT_FILL[actor.accent] : 'bg-gray-300',
        )}
      >
        {actor ? actor.name.charAt(0) : '·'}
      </span>
      <span className="truncate text-xs text-gray-500">{name}</span>
    </span>
  )
}

function ActivityRow({ entry }: { entry: CampaignActivityEntry }) {
  return (
    // Time and actor are fixed-width and together eat 260px, which is most of a
    // phone. Wrapping lets them share the first line and drops the entry itself
    // onto the next; from `sm` up all three sit on one row as designed.
    <li className="flex flex-wrap items-start gap-x-3.5 gap-y-1.5 border-b border-gray-200 px-4 py-3 last:border-b-0">
      <span className="w-27.5 shrink-0 text-xs text-gray-300">
        {entry.time}
      </span>
      <ActorCell actor={entry.actor} />
      <span className="flex min-w-full flex-1 flex-col gap-1 sm:min-w-0">
        <span
          className={cn(
            'text-sm font-medium',
            TITLE_TONE[entry.tone ?? CAMPAIGN_ACTIVITY_TONES.neutral],
          )}
        >
          {entry.title}
        </span>
        <span className="text-xs text-gray-300">{entry.detail}</span>
      </span>
    </li>
  )
}

interface CampaignActivityTabProps {
  campaignId: string
}

export function CampaignActivityTab({ campaignId }: CampaignActivityTabProps) {
  const { t } = useTranslation()
  const {
    groups,
    isLoading,
    chips,
    filter,
    onFilterChange,
    pagination,
    retention,
    onExport,
  } = useCampaignActivity(campaignId)

  return (
    <div className="flex flex-col gap-4">
      <CampaignFilterChips
        chips={chips}
        active={filter}
        onChange={onFilterChange}
      />
      <section className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {isLoading && <CampaignActivitySkeleton />}
        {!isLoading && groups.length === 0 && (
          <p className="px-4 py-8 text-center text-xs text-gray-300">
            {t('campaigns.activity.empty')}
          </p>
        )}
        {groups.map((group) => (
          <Fragment key={group.key}>
            <h3 className="border-b border-gray-200 bg-gray-50 px-4 py-2.5 text-2xs font-medium tracking-wider text-gray-300">
              {group.label}
            </h3>
            <ul>
              {group.entries.map((entry) => (
                <ActivityRow key={entry.id} entry={entry} />
              ))}
            </ul>
          </Fragment>
        ))}
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-300">{retention}</p>
          <button
            type="button"
            onClick={onExport}
            className="cursor-pointer text-xs font-medium text-campaign-running hover:underline"
          >
            {t('campaigns.activity.exportLog')}
          </button>
        </div>
      </section>
      {/* The feed is grouped by day rather than tabular, so it carries the
          pager under itself instead of inside a table footer. */}
      {pagination.totalPages > 1 && (
        <TablePagination
          bare
          page={pagination.page}
          pageSize={pagination.pageSize}
          totalItems={pagination.totalItems}
          totalPages={pagination.totalPages}
          onPageChange={pagination.onPageChange}
        />
      )}
    </div>
  )
}
